import api from './api';
import {
  BookingType,
  GetAllRequestDto,
  PaginatedResponse,
  CreateBookingTypeDto,
  UpdateBookingTypeDto,
} from '@/types/booking.types';

class BookingTypeService {
  private readonly basePath = '/booking-type';

  /**
   * Get all booking types with pagination and filtering
   */
  async getBookingTypes(params?: GetAllRequestDto): Promise<PaginatedResponse<BookingType>> {
    const response = await api.post(`${this.basePath}/list`, params || {});
    return response.data;
  }

  /**
   * Get booking type by ID
   */
  async getBookingTypeById(id: string): Promise<BookingType> {
    const response = await api.get(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Get booking types for a specific plan
   */
  async getPlanBookingTypes(planId: string): Promise<BookingType[]> {
    const response = await api.get(`${this.basePath}/plan/${planId}`);
    return response.data;
  }

  /**
   * Create a new booking type
   */
  async createBookingType(data: CreateBookingTypeDto): Promise<BookingType> {
    const response = await api.post(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing booking type
   */
  async updateBookingType(id: string, data: UpdateBookingTypeDto): Promise<BookingType> {
    const response = await api.put(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a booking type
   */
  async deleteBookingType(id: string): Promise<void> {
    await api.delete(`${this.basePath}/${id}`);
  }

  /**
   * Get active booking types for UI display
   * This is a convenience method for the frontend
   */
  async getActiveBookingTypes(locationId?: string): Promise<BookingType[]> {
    const params: GetAllRequestDto = {
      page: 1,
      limit: 100,
      filters: {
        active: true,
        ...(locationId && { locationId }),
      },
    };

    const response = await api.post(`${this.basePath}/list`, params);
    return response.data.data;
  }

  /**
   * Get booking types by facility type
   */
  async getBookingTypesByFacilityType(
    facilityType: string,
    locationId?: string
  ): Promise<BookingType[]> {
    const params: GetAllRequestDto = {
      page: 1,
      limit: 100,
      filters: {
        type: facilityType,
        active: true,
        ...(locationId && { locationId }),
      },
    };

    const response = await api.post(`${this.basePath}/list`, params);
    return response.data.data;
  }

  /**
   * Get booking types for a specific location
   * Uses the new endpoint: /api/member-locations/{locationId}/booking-types
   */
  async getBookingTypesByLocation(locationId: string): Promise<BookingType[]> {
    const response = await api.get(`/member-locations/${locationId}/booking-types`);
    return response.data;
  }
}

export const bookingTypeService = new BookingTypeService();
